import { useTableActionContext } from '@aibees/elephant/context/table-action/context';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { classnames } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { BulkPanelGuidanceTotalProps as Props } from './types';

export const BulkPanelGuidanceTotal = memo(({ className }: Props) => {
  const { basicPeople, aiPeople } = useGetCreditsAmountTypeOnActionPopup();
  // Effective modality: this "open Saved Records → Enrich" guidance only makes
  // sense for a bulk action off a table, which is the only flow that stays TOTAL.
  // A single-record flow resolves to the record's own saved state instead.
  const { effectiveModality } = useTableActionContext();

  // `basicPeople` alone excludes similarity searches, so a lookalike / AI people
  // search in Total Records never saw this at all while the Saved equivalent did.
  // The acceptance criterion is unconditional, hence `aiPeople` too.
  const shouldShow = useMemo(() => {
    return (
      (basicPeople || aiPeople) && effectiveModality === SearchModality.TOTAL
    );
  }, [basicPeople, aiPeople, effectiveModality]);

  if (!shouldShow) return null;

  return (
    <Styled.Wrapper
      className={classnames('BulkPanelGuidanceTotal', className)}
      sx={SX.root}
      severity="default"
      variant="outlined"
    >
      <Typography variant="body4" fontWeight={400} color="grey.600">
        Some selected contacts may already be saved. Saved contacts use the data
        currently stored in Saved Records. To refresh Work History / Job Change
        or Profile Insights, open Saved Records → Enrich and run Update All or
        the specific update action you need. Then return here to complete this
        action.
      </Typography>
    </Styled.Wrapper>
  );
});

BulkPanelGuidanceTotal.displayName = 'BulkPanelGuidanceTotal';
